<script lang="ts" setup>
import type { DnsRecord, DnsRecordCreateRequest } from '@/types'
import { computed, ref, watch } from 'vue'
import { STATUS_CODES } from '@/constants'
import {
  DNS_TTL_PRESETS,
  DNS_PRIORITY_TYPES,
  DNS_WEIGHT_TYPES,
  DNS_PORT_TYPES,
  DNS_DEFAULT_TTL,
  getEnabledDnsTypes
} from '@/constants/dnsPresets'
import { useAxios } from '@/composables/useAxios'
import { useDnsApiBase } from '@/composables/useDnsApiBase'
import { normalizeNameForApi, formatNameForDisplay } from '@/utils/dnsName'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { useI18n } from 'vue-i18n'
import BaseSlider from '@/components/base/BaseSlider.vue'

const props = defineProps<{
  show: boolean
  editRecord: DnsRecord | null
  domainId: number
  domainName: string
  apiBase?: string
}>()

const apiBase = useDnsApiBase(() => props.domainId, { override: () => props.apiBase })

const emits = defineEmits<{
  (e: 'add-update', state: { record: DnsRecord; isNew: boolean }): void
  (e: 'close-drawer', state: boolean): void
}>()

const notifyStore = useNotifyStore()
const { t } = useI18n()

// State
const isOpen = ref(false)
const isUpdate = ref(false)
const loading = ref(false)

// Dropdown state
const typeDropdownOpen = ref(false)
const ttlDropdownOpen = ref(false)

const formData = ref({
  type: 'A',
  name: '',
  content: '',
  ttl: DNS_DEFAULT_TTL,
  priority: 10,
  weight: 0,
  port: 0
})

// Computed
const showPriority = computed(() =>
  DNS_PRIORITY_TYPES.includes(formData.value.type as (typeof DNS_PRIORITY_TYPES)[number])
)

const showWeight = computed(() =>
  DNS_WEIGHT_TYPES.includes(formData.value.type as (typeof DNS_WEIGHT_TYPES)[number])
)

const showPort = computed(() =>
  DNS_PORT_TYPES.includes(formData.value.type as (typeof DNS_PORT_TYPES)[number])
)

const drawerTitle = computed(() =>
  isUpdate.value ? t('dashboard.domains.dns.editRecord') : t('dashboard.domains.dns.addRecord')
)

const enabledTypes = computed(() => getEnabledDnsTypes())

const selectedTypeLabel = computed(() => {
  const type = enabledTypes.value.find(tp => tp.value === formData.value.type)
  return type ? `${type.value} - ${t(type.descriptionKey)}` : formData.value.type
})

const selectedTtlLabel = computed(() => {
  const preset = DNS_TTL_PRESETS.find(p => p.value === formData.value.ttl)
  return preset ? t(preset.labelKey) : t('dashboard.domains.dns.ttlPresets.seconds', { value: formData.value.ttl })
})

const translatedTtlPresets = computed(() =>
  DNS_TTL_PRESETS.map(p => ({ value: p.value, label: t(p.labelKey) }))
)

// Record types that require FQDN content
const FQDN_REQUIRED_TYPES = ['MX', 'CNAME', 'NS']

// Convert to FQDN if not already (e.g., "mail" -> "mail.example.com")
function toFqdn(value: string): string {
  if (!value || value.includes('.')) return value
  return props.domainName ? `${value}.${props.domainName}` : value
}

// Methods
function getTypeColor(type: string): string {
  const colors: Record<string, string> = {
    A: '#2196f3',
    AAAA: '#3f51b5',
    CNAME: '#9c27b0',
    MX: '#ff9800',
    TXT: '#4caf50',
    NS: '#9e9e9e',
    SRV: '#e91e63',
    CAA: '#ffc107'
  }
  return colors[type] || '#9e9e9e'
}

function selectType(value: string) {
  formData.value.type = value
  typeDropdownOpen.value = false
}

function selectTtl(value: number) {
  formData.value.ttl = value
  ttlDropdownOpen.value = false
}

function closeAllDropdowns() {
  typeDropdownOpen.value = false
  ttlDropdownOpen.value = false
}

function resetForm() {
  formData.value = {
    type: 'A',
    name: '',
    content: '',
    ttl: DNS_DEFAULT_TTL,
    priority: 10,
    weight: 0,
    port: 0
  }
  isUpdate.value = false
  closeAllDropdowns()
}

function initializeForm() {
  if (props.editRecord) {
    isUpdate.value = true
    formData.value = {
      type: props.editRecord.type,
      name: formatNameForDisplay(props.editRecord.name, props.domainName),
      content: props.editRecord.content,
      ttl: props.editRecord.ttl || DNS_DEFAULT_TTL,
      priority: props.editRecord.priority || 10,
      weight: 0,
      port: 0
    }
  } else {
    isUpdate.value = false
    resetForm()
  }
}

function handleClose() {
  isOpen.value = false
  closeAllDropdowns()
  emits('close-drawer', false)
}

async function handleSubmit() {
  if (!formData.value.content.trim()) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: t('validation.required')
    })
    return
  }

  loading.value = true
  const nameValue = normalizeNameForApi(formData.value.name, props.domainName)

  let contentValue = formData.value.content

  // Auto-convert to FQDN for MX, CNAME, NS records
  if (FQDN_REQUIRED_TYPES.includes(formData.value.type)) {
    contentValue = toFqdn(contentValue.trim())
  }

  if (showPort.value && showWeight.value) {
    contentValue = `${formData.value.weight} ${formData.value.port} ${contentValue}`
  } else if (showWeight.value) {
    contentValue = `${formData.value.weight} ${contentValue}`
  }

  const requestData: DnsRecordCreateRequest = {
    type: formData.value.type as DnsRecordCreateRequest['type'],
    content: contentValue,
    name: nameValue || '',
    ttl: formData.value.ttl,
    priority: showPriority.value ? formData.value.priority : undefined
  }

  try {
    if (isUpdate.value && props.editRecord?.id) {
      const res = await useAxios<DnsRecordCreateRequest, DnsRecord>(
        'PUT',
        `${apiBase.value}/records/${props.editRecord.id}`,
        true,
        requestData
      )
      if (res?.status === STATUS_CODES.OK) {
        notifyStore.createNotify({
          id: uuid('v4'),
          alertType: 'success',
          title: t('common.success'),
          message: t('dashboard.domains.dns.recordUpdated')
        })
        emits('add-update', { record: res.data as DnsRecord, isNew: false })
        handleClose()
        resetForm()
      }
    } else {
      const res = await useAxios<DnsRecordCreateRequest, DnsRecord>(
        'POST',
        `${apiBase.value}/records`,
        true,
        requestData
      )
      if (res?.status === STATUS_CODES.CREATED || res?.status === STATUS_CODES.OK) {
        notifyStore.createNotify({
          id: uuid('v4'),
          alertType: 'success',
          title: t('common.success'),
          message: t('dashboard.domains.dns.recordAdded')
        })
        emits('add-update', { record: res.data as DnsRecord, isNew: true })
        handleClose()
        resetForm()
      }
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'DnsRecordAddEdit' })
    const axiosError = error as { response?: { status?: number; data?: { error?: string } } }
    let message: string
    if (axiosError.response?.status === STATUS_CODES.CONFLICT) {
      message =
        axiosError.response?.data?.error === 'dns_record_already_exists'
          ? t('dashboard.domains.dns.conflictRecordExists')
          : t('dashboard.domains.dns.conflictDyndnsManaged')
    } else {
      message = getErrorMessage(error)
    }
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message
    })
  } finally {
    loading.value = false
  }
}

// Watchers
watch(
  () => props.show,
  (show) => {
    isOpen.value = show
    if (show) {
      initializeForm()
    }
  },
  { immediate: true }
)
</script>

<template>
  <BaseSlider
    v-model="isOpen"
    :title="drawerTitle"
    :loading="loading"
    @close="handleClose"
  >
    <!-- Form Content -->
    <div class="dns-form" @click="closeAllDropdowns">
      <!-- Type Dropdown -->
      <div class="form-group">
        <label>Type</label>
        <div class="custom-select" @click.stop>
          <div class="select-trigger" @click="typeDropdownOpen = !typeDropdownOpen; ttlDropdownOpen = false">
            <span class="type-badge" :style="{ background: getTypeColor(formData.type) }">{{ formData.type }}</span>
            <span class="select-text">{{ selectedTypeLabel }}</span>
            <span class="select-arrow" :class="{ open: typeDropdownOpen }">&#9662;</span>
          </div>
          <div v-if="typeDropdownOpen" class="select-dropdown">
            <div
              v-for="typeOpt in enabledTypes"
              :key="typeOpt.value"
              class="select-option"
              :class="{ selected: formData.type === typeOpt.value }"
              @click="selectType(typeOpt.value)"
            >
              <span class="type-badge" :style="{ background: getTypeColor(typeOpt.value) }">{{ typeOpt.value }}</span>
              <span>{{ $t(typeOpt.descriptionKey) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Name -->
      <div class="form-group" @click.stop>
        <label>Name</label>
        <input v-model="formData.name" type="text" placeholder="@ or subdomain" @focus="closeAllDropdowns" />
      </div>

      <!-- Content -->
      <div class="form-group" @click.stop>
        <label>Content *</label>
        <input v-model="formData.content" type="text" placeholder="Value" @focus="closeAllDropdowns" />
      </div>

      <!-- TTL Dropdown -->
      <div class="form-group">
        <label>TTL</label>
        <div class="custom-select" @click.stop>
          <div class="select-trigger" @click="ttlDropdownOpen = !ttlDropdownOpen; typeDropdownOpen = false">
            <span class="select-text">{{ selectedTtlLabel }}</span>
            <span class="select-arrow" :class="{ open: ttlDropdownOpen }">&#9662;</span>
          </div>
          <div v-if="ttlDropdownOpen" class="select-dropdown">
            <div
              v-for="ttl in translatedTtlPresets"
              :key="ttl.value"
              class="select-option"
              :class="{ selected: formData.ttl === ttl.value }"
              @click="selectTtl(ttl.value)"
            >
              {{ ttl.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Priority -->
      <div v-if="showPriority" class="form-group" @click.stop>
        <label>Priority</label>
        <input v-model.number="formData.priority" type="number" min="0" @focus="closeAllDropdowns" />
      </div>

      <!-- Weight -->
      <div v-if="showWeight" class="form-group" @click.stop>
        <label>Weight</label>
        <input v-model.number="formData.weight" type="number" min="0" max="65535" @focus="closeAllDropdowns" />
      </div>

      <!-- Port -->
      <div v-if="showPort" class="form-group" @click.stop>
        <label>Port</label>
        <input v-model.number="formData.port" type="number" min="0" max="65535" @focus="closeAllDropdowns" />
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <button type="button" class="btn-cancel" @click="handleClose">Cancel</button>
      <button type="button" class="btn-save" :disabled="loading || !formData.content" @click="handleSubmit">
        {{ loading ? 'Saving...' : (isUpdate ? 'Save' : 'Add') }}
      </button>
    </template>
  </BaseSlider>
</template>

<style lang="scss" scoped>
.dns-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 24px;

  label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(200, 218, 255, 0.6);
    margin-bottom: 10px;
  }

  input {
    width: 100%;
    padding: 12px 14px;
    background: rgba(15, 23, 48, 0.5);
    border: 1px solid rgba(120, 163, 255, 0.15);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    outline: none;
    transition: all 0.2s ease;

    &::placeholder {
      color: rgba(200, 218, 255, 0.3);
    }

    &:focus {
      border-color: rgba(0, 176, 255, 0.4);
      background: rgba(15, 23, 48, 0.7);
    }
  }
}

/* Custom Select Dropdown */
.custom-select {
  position: relative;
}

.select-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(120, 163, 255, 0.3);
    background: rgba(15, 23, 48, 0.6);
  }
}

.select-text {
  flex: 1;
}

.select-arrow {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.8rem;
  transition: transform 0.2s ease;

  &.open {
    transform: rotate(180deg);
  }
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  background: rgb(20, 30, 55);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 100;
  animation: dropdownFadeIn 0.15s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.select-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
  }

  &.selected {
    background: rgba(0, 176, 255, 0.15);
    color: #64b5f6;
  }

  &:first-child {
    border-radius: 7px 7px 0 0;
  }

  &:last-child {
    border-radius: 0 0 7px 7px;
  }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

/* Footer Buttons */
.btn-cancel,
.btn-save {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  color: rgba(200, 218, 255, 0.7);

  &:hover {
    background: rgba(120, 163, 255, 0.12);
    border-color: rgba(120, 163, 255, 0.25);
  }
}

.btn-save {
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.25);
  color: #81c784;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
